/**
 * NewImportContactModal Component
 * ----------------------------------------------------------
 * PURPOSE:
 * This modal handles importing **Contact Data** into the CRM system
 * via CSV/Excel uploads. It supports:
 *
 *  ✔ Viewing previous contact import logs
 *  ✔ Uploading new CSV/XLS/XLSX files
 *  ✔ Downloading a correct CSV template based on configurable headers
 *
 * HOW IT WORKS:
 * 1. Fetch previous uploads from Firestore (fetchContactsFile)
 * 2. User uploads file → backend processes → UI updates status
 * 3. CSVGenerator allows downloading the correct template
 *
 * PROPS:
 *  open            → Controls modal visibility
 *  close           → Function to close modal
 *  organization_id → ID used for fetching & uploading data
 *  user            → Logged-in user (used for UID & uploader name)
 *  conatctHeader   → Dynamic CRM contact header list (Redux)
 *  theme           → Dark/light mode
 *
 * SERVICES USED:
 *  fetchContactsFile() → Fetch contact import history
 *  uploadContactsFile() → Upload new contact CSV file
 */

import { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";

import { fetchContactsFile, uploadContactsFile } from "../../../Services/contacts";
import { useDispatch, connect } from "react-redux";
import Loading from "../../Loading/Loading";
import moment from "moment";
import CSVGenerator from "./CSVGenerator";
import { themeColors } from "../../theme";
import "../../../theme-vars.css";
import { useMediaQuery, useTheme } from "@mui/material";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import { Circles } from "react-loader-spinner";

type props = {
  open: boolean;
  close: () => void;
  organization_id: string;
  history: any;
  usersList: any[];
  user: any;
  conatctHeader: any;
  theme: any;
};

interface DataObject {
  [key: string]: any;
}

const NewImportContactModal: FunctionComponent<props> = ({
  open,
  close,
  organization_id,
  history,
  usersList,
  user,
  conatctHeader,
  theme
}) => {

  /** MUI theme + responsiveness */
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const filtervalues = ["customer_name", "country_code", "contact_no", "alternate_no", "email", "stage", "contact_owner_email", "lead_source", "budget", "call_back_reason", "project", "property_type", "property_sub_type", "property_stage", "location", "lost_reason", "not_int_reason", "other_lost_reason", "other_not_int_reason", "next_follow_up_type", "next_follow_up_date_time", "created_by", "created_at", "lead_assign_time", "stage_change_at", "addset", "campaign", "notes", "lead_type", "field_one", "field_two", "field_three", "field_four", "field_five", "field_six"];
  const reqData = conatctHeader.filter((obj: any) => filtervalues.includes(obj.label));

  /** Add additional display-only fields for template generation */
  const dbData = [...reqData, { label: "notes", value: "Notes" }];

  /** Convert to CSV header row */
  const headerData = dbData.map((item: any) => item.value);
  const contactHeaderData: DataObject[] = [];
  contactHeaderData.push(headerData);
  const [load, setLoad] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const dispatcher = useDispatch();

  /**
   * FETCH PREVIOUS CONTACT IMPORT LOGS
   * ------------------------------------------------------------------
   * List of uploaded files (with status, timestamp, result link).
   */
  useEffect(() => {
    if (organization_id) {
      const unsub = fetchContactsFile(
        organization_id,
        user.uid,
        (data: any) => setUploadedFiles(data),
        dispatcher
      );
      return () => unsub();
    }
  }, [organization_id]);

  /**
   * UPLOAD HANDLER
   * ------------------------------------------------------------------
   * Triggers backend processing of the uploaded CSV file.
   */
  const upload = async (file: any) => {
    setLoad(true);

    uploadContactsFile(
      file,
      organization_id,
      user.uid,
      dispatcher,
      (data: boolean) => setLoad(data),
      `${user.user_first_name} ${user.user_last_name}`
    );
  };

  /** Reusable row styling based on light/dark theme */
  const tdStyles = {
    backgroundColor: theme ? themeColors.tdBackgroundColor : themeColors.tdBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  };

  const thStyles = {
    backgroundColor: theme ? themeColors.thBackgroundColor : themeColors.thBackgroundColorDark,
  };

  /** Modal Styling - custom layout overrides */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    content: {
      background: theme ? themeColors.cardBackgroundColor : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "10px 20px",
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      margin: "auto",
      inset: "auto"
    }
  };

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        <div className={styles.child}>

          {/* Optional global loader */}
          {load && <Loading />}

          {/* TITLE */}
          <h5
            style={{
              fontFamily: "var(--font-family-main)",
              fontSize: "var(--font-size-large)",
              paddingTop: isMobile ? "unset" : "15px"
            }}
          >
            Import Data for "Contact Form"
          </h5>

          {/* TABLE: PREVIOUS IMPORT HISTORY */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead
                className={styles.head}
                style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}
              >
                <tr>
                  <th className={styles.th} style={thStyles}>Sno.</th>
                  <th className={styles.th} style={thStyles}>Request Id</th>
                  <th className={styles.th} style={thStyles}>Created At</th>
                  <th className={styles.th} style={thStyles}>Status</th>
                  <th className={styles.th} style={thStyles}>Upload Count</th>
                  <th className={styles.th} style={thStyles}>Uploaded File</th>
                  <th className={styles.th} style={thStyles}>Processed File</th>
                </tr>
              </thead>

              <tbody>
                {uploadedFiles.map((item: any, index: number) => (
                  <tr key={index} className={styles.no}>
                    <td className={styles.td} style={tdStyles}>{index + 1}</td>
                    <td className={styles.td} style={tdStyles}>{item.request_id}</td>

                    <td className={styles.td} style={tdStyles}>
                      {moment(item.created_at.toDate()).format("MM/DD/YY HH:mm")}
                    </td>

                    <td className={styles.td} style={tdStyles}>{item.status}</td>

                    <td className={styles.td} style={tdStyles}>{item.uploadCount}</td>

                    <td className={styles.td} style={tdStyles}>
                      <a href={item.fileUrl}>Uploaded File</a>
                    </td>

                    <td className={styles.td} style={tdStyles}>
                      {item.responseURL !== "" && (
                        <a href={item.responseURL}>Processed File</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* FOOTER ACTIONS */}
          <div className={styles.selectBox}>

            {/* CANCEL BUTTON */}
            <div style={{ marginRight: 8 }}>
              <StyledCancelButton onClick={close}>
                Cancel
              </StyledCancelButton>
            </div>

            {/* DOWNLOAD TEMPLATE */}
            <div style={{ marginRight: 8 }}>
              <CSVGenerator data={contactHeaderData} />
            </div>

            {/* UPLOAD BUTTON */}
            <div>
              <input
                id="file-input"
                className={styles.select}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e: any) => upload(e.target.files[0])}
              />

              <StyledSubmitButton
                onClick={() => document.getElementById("file-input")?.click()}
                isMobile={isMobile}
              >
                {load ? (
                  <span style={{ display: "flex", justifyContent: "center" }}>
                    <Circles color="#fff" height={20} width={20} /> Upload...
                  </span>
                ) : (
                  <>Upload File</>
                )}
              </StyledSubmitButton>
            </div>
          </div>

          {/* FOOTER NOTES */}
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <p style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}>
              <i className="fas fa-info-circle"></i> Only .csv file type is supported.
            </p>
            <p
              style={{
                marginTop: "-1rem",
                fontFamily: "var(--font-family-main)",
                fontSize: "var(--font-size-small)"
              }}
            >
              <i className="fas fa-info-circle"></i> Please ensure all mandatory fields are filled.
            </p>
          </div>

        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  conatctHeader: state.contactHeaderList.data,
  theme: state.theme.isLightMode
});

export default connect(mapStateToProps)(NewImportContactModal);
